import { boundedString, httpUrl, isRecord } from '../../../internal/shell/embed.js';
/**
 * The Rook Chat embed protocol (`rook-chat-embed` v1), host side: the only
 * contract between a Rook app (the host, which draws the dock) and Rook Chat
 * running inside the dock's iframe. Mirrors Rook Chat's own protocol file.
 *
 * Every message is `{ protocol, v, type, ...fields }`, posted to an exact
 * origin. The host checks `event.origin` against its allow list and
 * `event.source` against the frame's window, then decodes the data; anything
 * malformed or unknown is dropped.
 *
 * Host → embed: init, open, context, visibility, refresh-result.
 * Embed → host: ready, unread, layout, ring, close, refresh-request, status, location.
 *
 * The embed never refreshes the session itself: it sends `refresh-request` and
 * the host answers with its own single-flight refresh (Rook ID rotates refresh
 * tokens; two racing refreshes sign the person out).
 */
export const CHAT_EMBED_PROTOCOL = 'rook-chat-embed';
export const CHAT_EMBED_VERSION = 1;
const LAYOUTS = ['panel', 'expanded', 'call', 'fullscreen'];
const STATUSES = ['ok', 'signed-out', 'unavailable'];
const ID = /^[A-Za-z0-9_-]{1,64}$/;
const id = (v) => boundedString(v, 64) && ID.test(v);
/** The wire form of a host message. */
export function encodeChatEmbedMessage(message) {
    return { ...message, protocol: CHAT_EMBED_PROTOCOL, v: CHAT_EMBED_VERSION };
}
/** A message from the embed, or null when it is malformed, unknown or a host message. */
export function decodeChatEmbedMessage(data) {
    if (!isRecord(data))
        return null;
    if (data.protocol !== CHAT_EMBED_PROTOCOL || data.v !== CHAT_EMBED_VERSION)
        return null;
    const d = data;
    switch (d.type) {
        case 'rook-chat:ready':
        case 'rook-chat:close':
            return { type: d.type };
        case 'rook-chat:unread':
            return typeof d.count === 'number' &&
                Number.isInteger(d.count) &&
                d.count >= 0 &&
                d.count < 1e6
                ? { type: d.type, count: d.count }
                : null;
        case 'rook-chat:layout':
            return LAYOUTS.includes(d.layout)
                ? { type: d.type, layout: d.layout }
                : null;
        case 'rook-chat:ring':
            return boundedString(d.callId, 64) &&
                boundedString(d.conversationId, 64) &&
                boundedString(d.from, 64)
                ? { type: d.type, callId: d.callId, conversationId: d.conversationId, from: d.from }
                : null;
        case 'rook-chat:refresh-request':
            return id(d.id) ? { type: d.type, id: d.id } : null;
        case 'rook-chat:status':
            return STATUSES.includes(d.status)
                ? { type: d.type, status: d.status }
                : null;
        case 'rook-chat:location':
            if (d.conversationId === null)
                return { type: d.type, conversationId: null };
            return id(d.conversationId) ? { type: d.type, conversationId: d.conversationId } : null;
        default:
            return null;
    }
}
/** Drops an `open` detail that the embed would refuse (bad ids or non-http URLs). */
export function cleanOpenDetail(detail) {
    const out = {};
    if (!detail)
        return out;
    if (detail.conversationId !== undefined && id(detail.conversationId))
        out.conversationId = detail.conversationId;
    if (detail.share && boundedString(detail.share.title, 300) && httpUrl(detail.share.url))
        out.share = { title: detail.share.title, url: detail.share.url };
    const c = detail.create;
    if (c &&
        (c.kind === 'task' || c.kind === 'ticket') &&
        boundedString(c.title, 300) &&
        httpUrl(c.url))
        out.create = { kind: c.kind, title: c.title, url: c.url };
    return out;
}
/** The page the person is on, for the embed: URL without fragment, document title. */
export function chatPageContext(app) {
    const u = new URL(window.location.href);
    u.hash = '';
    const title = document.title.trim().slice(0, 300);
    return { app, url: u.toString(), ...(title ? { title } : {}) };
}
/**
 * The host side of the protocol, framework-free: listens only to the frame's
 * own window at an allowed origin, never posts to `*`, queues messages until
 * the frame is ready, and answers refresh requests with the host's refresh.
 */
export function createChatDockBridge(opts) {
    let ready = false;
    const outbox = [];
    const target = () => opts.targetOrigin && opts.allowedOrigins.has(opts.targetOrigin) ? opts.targetOrigin : null;
    const send = (win, m) => {
        const to = target();
        if (!to)
            return;
        try {
            win.postMessage(encodeChatEmbedMessage(m), to);
        }
        catch {
            /* the frame navigated away */
        }
    };
    return {
        handle(e) {
            const win = opts.frameWindow();
            if (!win || e.source !== win || !opts.allowedOrigins.has(e.origin))
                return false;
            const m = decodeChatEmbedMessage(e.data);
            if (!m)
                return false;
            if (m.type === 'rook-chat:ready') {
                send(win, { type: 'rook-chat:init', app: opts.appName(), workspaceId: opts.workspaceId() });
                send(win, { type: 'rook-chat:context', ...opts.context() });
                ready = true;
                for (const queued of outbox.splice(0))
                    send(win, queued);
            }
            else if (m.type === 'rook-chat:refresh-request') {
                Promise.resolve()
                    .then(() => opts.refresh())
                    .then(() => send(win, { type: 'rook-chat:refresh-result', id: m.id, ok: true }), () => send(win, { type: 'rook-chat:refresh-result', id: m.id, ok: false }));
            }
            opts.on(m);
            return true;
        },
        post(message) {
            const win = opts.frameWindow();
            if (win && ready)
                send(win, message);
            else
                outbox.push(message);
        },
        isReady: () => ready,
        reset() {
            ready = false;
        },
    };
}
//# sourceMappingURL=chat-dock.protocol.js.map